import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  fetchOfficials, fetchStats,
  US_STATES, partyBg, levelBadge, personTypeBadge, personTypeLabel, PERSON_TYPES,
  type Official, type OfficialLifecycleStatus, type Pagination, type StatsResponse, type OfficialsFilters,
} from './apiClient';
import OfficialAvatar from './src/components/OfficialAvatar';
import AdoptButton from './src/components/AdoptButton';
import { buildOfficialLifecycleSummary } from './officialListLifecycle';
import { buildFeaturedOfficialAction, chooseFeaturedOfficial, type FeaturedOfficialAction } from './homepageFeaturedAction';
import { SpineScoreGauge } from './src/components/SpineScore';
import { CaseFileScoreRow } from './src/components/casefile/CaseFileScoreModule';
import { useCaseFileStyle } from './src/context/CaseFileStyleContext';

const OfficialsPage: React.FC = () => {
  const { style: caseFileStyle } = useCaseFileStyle();
  const [searchParams] = useSearchParams();
  const [officials, setOfficials] = useState<Official[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [personType, setPersonType] = useState('');
  const [level, setLevel] = useState(() => searchParams.get('level') || '');
  const [officeBranch, setOfficeBranch] = useState('');
  const [state, setState] = useState('');
  const [party, setParty] = useState('');
  const [chamber, setChamber] = useState('');
  const [hasDossier, setHasDossier] = useState('');
  const [lifecycleStatus, setLifecycleStatus] = useState('');
  const [sort, setSort] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const loadOfficials = useCallback(async (filters: OfficialsFilters) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOfficials(filters);
      setOfficials(data.officials);
      setPagination(data.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load officials');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load stats once
  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, []);

  // Load officials when filters change
  useEffect(() => {
    loadOfficials({
      search: search || undefined,
      person_type: personType || undefined,
      level: level || undefined,
      office_branch: officeBranch || undefined,
      state: state || undefined,
      party: party || undefined,
      chamber: chamber || undefined,
      has_dossier: hasDossier === 'true' || undefined,
      lifecycle_status: lifecycleStatus && lifecycleStatus !== 'all' ? lifecycleStatus as OfficialLifecycleStatus : undefined,
      include_archived: lifecycleStatus === 'all' || undefined,
      sort: (sort || undefined) as any,
      page,
      limit: 50,
    });
  }, [personType, level, officeBranch, state, party, chamber, hasDossier, lifecycleStatus, sort, page, loadOfficials]);

  // Debounced search
  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      loadOfficials({
        search: val || undefined,
        person_type: personType || undefined,
        level: level || undefined,
        office_branch: officeBranch || undefined,
        state: state || undefined,
        party: party || undefined,
        chamber: chamber || undefined,
        has_dossier: hasDossier === 'true' || undefined,
        lifecycle_status: lifecycleStatus && lifecycleStatus !== 'all' ? lifecycleStatus as OfficialLifecycleStatus : undefined,
        include_archived: lifecycleStatus === 'all' || undefined,
        page: 1,
        limit: 50,
      });
    }, 350);
  };

  const handleFilterChange = (setter: (v: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setPersonType('');
    setLevel('');
    setOfficeBranch('');
    setState('');
    setParty('');
    setChamber('');
    setHasDossier('');
    setLifecycleStatus('');
    setPage(1);
  };

  const hasFilters = search || personType || level || officeBranch || state || party || chamber || hasDossier || lifecycleStatus;
  // Build featured actions from officials that have the most engagement signals
  const featuredActions = useMemo<FeaturedOfficialAction[]>(() => {
    const sorted = [...officials]
      .filter(o => o.accountability_score != null || o.adoption_count)
      .sort((a, b) => {
        const aScore = (a.adoption_count ?? 0) * 10 + (a.accountability_score ?? 0);
        const bScore = (b.adoption_count ?? 0) * 10 + (b.accountability_score ?? 0);
        return bScore - aScore;
      })
      .slice(0, 5);
    return sorted.map(o => buildFeaturedOfficialAction(o)).filter(Boolean);
  }, [officials]);

  // Rotate featured official every 8 seconds
  const [featuredIndex, setFeaturedIndex] = useState(0);
  useEffect(() => {
    if (featuredActions.length <= 1) return;
    const timer = setInterval(() => {
      setFeaturedIndex(prev => (prev + 1) % featuredActions.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [featuredActions.length]);

  const featuredAction = featuredActions[featuredIndex] ?? null;

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Header — lean directory header, not a repeat of the homepage hero.
          The full "ADOPT-A-COWARD" title, featured case, and leaderboard
          already live on the homepage; repeating them here just pushed the
          actual search/filter tools below the fold. */}
      <section className="max-w-5xl mx-auto text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-black text-navy-900 dark:text-cream-50 mb-2 tracking-tight uppercase">
          Officials Database
        </h1>
        <p className="text-navy-500 dark:text-navy-300 text-xs font-black uppercase tracking-[0.2em] mb-6 mono">
          {stats ? stats.total.toLocaleString() : '...'} Officials Tracked{stats ? ` · ${stats.dossiersCompleted.toLocaleString()} Dossiers` : ''}
        </p>

        {/* Federal / State quick nav — the two things people actually want
            to jump between, front and center instead of buried in a select. */}
        {stats && (
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <button
              onClick={() => handleFilterChange(setLevel, level === 'federal' ? '' : 'federal')}
              className={`px-6 py-3 rounded-xl border-2 font-black text-sm uppercase tracking-wider transition ${level === 'federal' ? 'bg-amber-600 border-amber-600 text-white' : 'bg-white dark:bg-navy-800 border-amber-300 dark:border-amber-800/50 text-amber-700 dark:text-amber-400 hover:border-amber-500'}`}
            >
              Federal <span className="opacity-70 font-bold">· {stats.byLevel.find((l) => l.office_level === 'federal')?.count.toLocaleString() ?? 0}</span>
            </button>
            <button
              onClick={() => handleFilterChange(setLevel, level === 'state' ? '' : 'state')}
              className={`px-6 py-3 rounded-xl border-2 font-black text-sm uppercase tracking-wider transition ${level === 'state' ? 'bg-teal-600 border-teal-600 text-white' : 'bg-white dark:bg-navy-800 border-teal-300 dark:border-teal-800/50 text-teal-700 dark:text-teal-400 hover:border-teal-500'}`}
            >
              State <span className="opacity-70 font-bold">· {stats.byLevel.find((l) => l.office_level === 'state')?.count.toLocaleString() ?? 0}</span>
            </button>
            {level && (
              <button
                onClick={() => handleFilterChange(setLevel, '')}
                className="px-4 py-3 rounded-xl border-2 border-cream-300 dark:border-navy-600 text-navy-400 dark:text-navy-300 font-bold text-sm uppercase tracking-wider hover:border-red-400 hover:text-red-500 transition"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-2xl mx-auto mb-6">
          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-300 dark:text-navy-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-12 pr-6 py-4 bg-white dark:bg-navy-800/80 rounded-lg border border-cream-300 dark:border-navy-600 text-navy-900 dark:text-cream-50 font-medium focus:border-gold-500 outline-none transition text-base"
          />
        </div>

        {/* Filter Section - Mobile Optimized */}
        <div className="mb-4">
          {/* Mobile Filter Toggle Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded-lg mb-3 text-navy-700 dark:text-cream-200 font-bold text-sm uppercase tracking-wide active:bg-cream-100 dark:active:bg-navy-700 transition min-h-[48px]"
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters {hasFilters && `(${[personType, level, officeBranch, state, party, chamber, hasDossier, lifecycleStatus].filter(Boolean).length})`}
            </span>
            <svg className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Filter Grid - Responsive Layout */}
          <div className={`${showFilters ? 'grid' : 'hidden md:grid'} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-8 gap-3`}>
            <select
              value={personType}
              onChange={(e) => handleFilterChange(setPersonType, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">All Types</option>
              {PERSON_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            <select
              value={level}
              onChange={(e) => handleFilterChange(setLevel, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">All Levels</option>
              <option value="federal">Federal</option>
              <option value="state">State</option>
              <option value="local">Local</option>
            </select>

            <select
              value={officeBranch}
              onChange={(e) => handleFilterChange(setOfficeBranch, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">All Branches</option>
              <option value="executive">Executive</option>
              <option value="legislative">Legislative</option>
              <option value="judicial">Judicial</option>
            </select>

            <select
              value={state}
              onChange={(e) => handleFilterChange(setState, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">All States</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
              ))}
            </select>

            <select
              value={party}
              onChange={(e) => handleFilterChange(setParty, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">All Parties</option>
              <option value="Republican">Republican</option>
              <option value="Democrat">Democrat</option>
              <option value="Democratic">Democratic</option>
              <option value="Independent">Independent</option>
              <option value="Libertarian">Libertarian</option>
            </select>

            <select
              value={chamber}
              onChange={(e) => handleFilterChange(setChamber, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">All Chambers</option>
              <option value="Senate">Senate</option>
              <option value="House">House</option>
              <option value="upper">Upper</option>
              <option value="lower">Lower</option>
            </select>

            <select
              value={hasDossier}
              onChange={(e) => handleFilterChange(setHasDossier, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">All Profiles</option>
              <option value="true">Has Dossier</option>
            </select>

            <select
              value={lifecycleStatus}
              onChange={(e) => handleFilterChange(setLifecycleStatus, e.target.value)}
              className="mobile-select bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">Active Only</option>
              <option value="vying">Vying Only</option>
              <option value="inactive">Inactive Only</option>
              <option value="all">All Lifecycle States</option>
              <option value="archived">Archived Only</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 w-full sm:w-auto mobile-button bg-red-50 dark:bg-red-600/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 rounded-lg px-6 py-3 sm:py-2.5 text-sm sm:text-xs font-black uppercase tracking-wider hover:bg-red-600 hover:text-white hover:border-red-600 active:bg-red-700 transition"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {/* Result count + Sort */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
          <p className="text-navy-400 dark:text-navy-300 text-[10px] font-black uppercase tracking-widest">
            {pagination.total.toLocaleString()} results — page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest">Sort</span>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-700 dark:text-cream-200 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition"
            >
              <option value="">Name A-Z</option>
              <option value="score_desc">Highest Spine</option>
              <option value="score_asc">Lowest Spine</option>
              <option value="adoptions_desc">Most Adopted</option>
              <option value="state">By State</option>
              <option value="recent">Most Recent</option>
            </select>
          </div>
        </div>
      </section>

      {/* Error */}
      {error && (
        <div className="max-w-2xl mx-auto mb-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-4 text-center">
          <p className="text-red-600 dark:text-red-400 text-sm font-bold">{error}</p>
          <button onClick={() => loadOfficials({ search: search || undefined, person_type: personType || undefined, level: level || undefined, office_branch: officeBranch || undefined, state: state || undefined, party: party || undefined, chamber: chamber || undefined, has_dossier: hasDossier === 'true' || undefined, lifecycle_status: lifecycleStatus && lifecycleStatus !== 'all' ? lifecycleStatus as OfficialLifecycleStatus : undefined, include_archived: lifecycleStatus === 'all' || undefined, page, limit: 50 })} className="text-red-500 text-xs font-black uppercase mt-2 hover:text-red-700 dark:hover:text-white transition">
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-gold-500 rounded-full animate-ping"></div>
            <span className="text-[10px] font-black text-navy-400 dark:text-navy-300 uppercase tracking-widest animate-pulse">Querying Database...</span>
          </div>
        </div>
      )}

      {/* Results Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {officials.map((official) => (
            <OfficialCard key={official.id} official={official} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && officials.length === 0 && !error && (
        <div className="text-center py-20">
          <p className="text-navy-400 dark:text-navy-300 text-sm font-black uppercase tracking-widest">No officials found matching your criteria.</p>
        </div>
      )}

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-12">
          <button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="px-4 py-2 bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded text-xs font-black uppercase text-navy-500 dark:text-cream-300 hover:border-gold-500 hover:text-gold-600 dark:hover:text-gold-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Prev
          </button>

          {generatePageNumbers(page, pagination.totalPages).map((p, i) =>
            p === '...' ? (
              <span key={`ellipsis-${i}`} className="px-2 text-navy-300 dark:text-navy-400 text-xs">...</span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(Number(p))}
                className={`w-10 h-10 rounded text-xs font-black uppercase transition ${
                  Number(p) === page
                    ? 'bg-navy-800 dark:bg-gold-500 text-white dark:text-navy-900 border border-navy-800 dark:border-gold-500'
                    : 'bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 text-navy-500 dark:text-cream-300 hover:border-gold-500 hover:text-gold-600'
                }`}
              >
                {p}
              </button>
            )
          )}

          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 bg-white dark:bg-navy-800 border border-cream-300 dark:border-navy-600 rounded text-xs font-black uppercase text-navy-500 dark:text-cream-300 hover:border-gold-500 hover:text-gold-600 dark:hover:text-gold-400 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const LeaderboardSection: React.FC = () => {
  const { style } = useCaseFileStyle();
  const isReport = style === 'field-report';
  const [leaders, setLeaders] = useState<Official[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch('/api/adoptions/leaderboard?limit=5');
        if (resp.ok) {
          const data = await resp.json();
          setLeaders(data.leaders || []);
        }
      } catch {
        // silent fail - leaderboard is non-critical
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || leaders.length === 0) return null;

  return (
    <div className={isReport
      ? 'max-w-4xl mx-auto mb-8 bg-white border border-navy-900 p-6'
      : 'max-w-4xl mx-auto mb-8 bg-cream-50 dark:bg-navy-800/50 border border-cream-300 dark:border-navy-600 rounded-xl p-6'
    }>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">🏆</span>
        <h2 className={`text-lg font-black uppercase tracking-wide ${isReport ? 'text-navy-900 mono' : 'text-navy-700 dark:text-cream-100'}`}>Most Adopted</h2>
      </div>
      <div className="space-y-2">
        {leaders.map((leader, i) => (
          <Link
            key={leader.id}
            to={`/officials/${leader.id}`}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-cream-100 dark:hover:bg-navy-700 transition group"
          >
            <span className="text-lg font-black text-gold-600 w-6 text-center">{i + 1}</span>
            <div className="w-8 h-8 rounded-full bg-cream-200 dark:bg-navy-700 flex items-center justify-center text-sm font-black text-navy-500 dark:text-cream-300 shrink-0 overflow-hidden">
              {leader.photo_url ? (
                <img src={leader.photo_url} alt={leader.name} className="w-full h-full object-cover rounded-full" />
              ) : (
                (leader.name || '?').charAt(0)
              )}
            </div>
            <div className="flex-grow min-w-0">
              <span className="font-bold text-navy-700 dark:text-cream-200 group-hover:text-gold-600 transition text-sm">
                {leader.name}
              </span>
              <span className="text-xs text-navy-400 dark:text-cream-400 ml-2">
                {leader.office_name} · {leader.state}
              </span>
            </div>
            <span className="text-sm font-black text-gold-600 shrink-0">
              {leader.adoption_count} 📣
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const FeaturedOfficialActionCard: React.FC<{ action: FeaturedOfficialAction; rotationDots?: number; currentIdx?: number; onDotClick?: (i: number) => void }> = ({ action, rotationDots, currentIdx, onDotClick }) => {
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'error'>('idle');
  const { official, whyItMatters, adoptionExplanation, shareText, shareTitle, shareUrlPath } = action;
  const lifecycle = buildOfficialLifecycleSummary(official);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${shareUrlPath}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        setShareState('copied');
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setShareState('copied');
        return;
      }

      setShareState('error');
    } catch {
      setShareState('error');
    } finally {
      window.setTimeout(() => setShareState('idle'), 2200);
    }
  };

  const { style } = useCaseFileStyle();
  const isReport = style === 'field-report';

  return (
    <section className="max-w-5xl mx-auto mb-8 text-left">
      <div className={isReport
        ? 'border-2 border-gold-500 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-cream-50 shadow-2xl overflow-hidden'
        : 'rounded-3xl border border-gold-500/30 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-cream-50 shadow-2xl overflow-hidden'
      }>
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-0">
          <div className="p-6 sm:p-8 lg:p-10">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={`text-[9px] sm:text-[10px] font-black uppercase tracking-[0.3em] text-gold-400 ${isReport ? 'mono' : ''}`}>{isReport ? 'Priority Case File' : 'Featured Accountability Action'}</span>
              {official.state && (
                <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-white/10 border border-white/10 text-cream-100">{official.state}</span>
              )}
              {lifecycle.badgeLabel && (
                <span className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-sky-500/20 border border-sky-400/20 text-sky-200">{lifecycle.badgeLabel}</span>
              )}
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase leading-[0.9] mb-3">
              Keep {official.name} under the microscope.
            </h2>
            <p className="text-sm sm:text-base text-cream-200/90 font-semibold uppercase tracking-wider mb-4">
              {official.office_name}{official.state ? ` — ${official.state}` : ''}
            </p>
            <p className="text-sm sm:text-base leading-relaxed text-cream-100/90 mb-5 max-w-2xl">
              {whyItMatters}
            </p>

            <div className="rounded-2xl border border-gold-500/20 bg-white/5 p-4 mb-6">
              <p className="text-[10px] font-black uppercase tracking-[0.25em] text-gold-400 mb-2">Why adopt a coward?</p>
              <p className="text-sm text-cream-100/85 leading-relaxed">{adoptionExplanation}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={`/officials/${encodeURIComponent(official.id)}`}
                className="inline-flex items-center justify-center rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 px-5 py-3 text-sm font-black uppercase tracking-widest transition"
              >
                View Dossier
              </Link>
              <div className="inline-flex items-center justify-center">
                <AdoptButton officialId={official.id} initialAdoptionCount={official.adoption_count ?? 0} />
              </div>
              <button
                onClick={handleShare}
                className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-cream-50 px-5 py-3 text-sm font-black uppercase tracking-widest transition"
              >
                {shareState === 'copied' ? 'Share Ready' : shareState === 'error' ? 'Share Failed' : 'Share This Dossier'}
              </button>
            </div>
          </div>

          <div className="border-t lg:border-t-0 lg:border-l border-white/10 bg-black/10 p-6 sm:p-8 flex flex-col justify-between gap-5">
            <div className="flex items-center gap-4">
              <OfficialAvatar
                name={official.name}
                photoUrl={official.photo_url}
                className="w-20 h-20 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0"
                imgClassName="w-20 h-20 rounded-2xl object-cover border border-white/10"
                initialsClassName="text-xl font-black text-cream-100 uppercase"
              />
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-gold-400 mb-1">Current Spine Report</p>
                <p className="text-sm text-cream-200/85 leading-relaxed">
                  {official.accountability_score != null
                    ? `Score: ${official.accountability_score}/100`
                    : 'Pending analysis'}
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <SpineScoreGauge score={official.accountability_score ?? null} size={120} forceLight />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <MetricCard label="Adoptions" value={String(official.adoption_count ?? 0)} />
              <MetricCard label="Source" value={official.source.replace(/_/g, ' ')} />
            </div>

            {rotationDots && rotationDots > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2">
                {Array.from({ length: rotationDots }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => onDotClick?.(i)}
                    className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'w-6 bg-gold-500' : 'w-1.5 bg-white/20 hover:bg-white/40'}`}
                    aria-label={`Featured official ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const MetricCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-cream-300/70 mb-1">{label}</p>
    <p className="text-base sm:text-lg font-black uppercase tracking-tight text-cream-50 break-words">{value}</p>
  </div>
);

const OfficialCard: React.FC<{ official: Official }> = ({ official }) => {
  const lifecycle = buildOfficialLifecycleSummary(official);
  const lifecycleBadgeClass = lifecycle.badgeTone === 'archived'
    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-300 border-amber-500/30'
    : lifecycle.badgeTone === 'inactive'
      ? 'bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/30'
      : lifecycle.badgeTone === 'vying'
        ? 'bg-sky-500/10 text-sky-600 dark:text-sky-300 border-sky-500/30'
        : '';

  return (
    <Link
      to={`/officials/${encodeURIComponent(official.id)}`}
      className="bg-white dark:bg-navy-800/60 rounded-xl border border-cream-300 dark:border-navy-600 p-5 hover:border-gold-500 transition group block shadow-sm"
    >
      <div className="flex items-start gap-4 mb-3">
        <OfficialAvatar
          name={official.name}
          photoUrl={official.photo_url}
          className="w-12 h-12 rounded-lg bg-cream-200 dark:bg-navy-700 border border-cream-300 dark:border-navy-600 flex items-center justify-center shrink-0"
          imgClassName="w-12 h-12 rounded-lg object-cover border border-cream-300 dark:border-navy-600 grayscale group-hover:grayscale-0 transition shrink-0"
          initialsClassName="text-sm font-black text-navy-400 dark:text-navy-300 uppercase"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-black text-navy-900 dark:text-cream-50 uppercase tracking-tight truncate group-hover:text-gold-600 dark:group-hover:text-gold-400 transition">{official.name}</h3>
          <p className="text-[10px] text-navy-400 dark:text-navy-300 font-bold uppercase tracking-wider truncate">{official.office_name}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {official.person_type && official.person_type !== 'official' && (
          <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${personTypeBadge(official.person_type)}`}>
            {personTypeLabel(official.person_type)}
          </span>
        )}
        {official.office_level && (
          <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${levelBadge(official.office_level)}`}>
            {official.office_level}
          </span>
        )}
        {lifecycle.badgeLabel && (
          <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${lifecycleBadgeClass}`}>
            {lifecycle.badgeLabel}
          </span>
        )}
        {official.party && (
          <span className={`text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${partyBg(official.party)}`}>
            {official.party}
          </span>
        )}
        {official.state && (
          <span className="text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded border bg-cream-100 dark:bg-navy-700/60 text-navy-500 dark:text-navy-300 border-cream-300 dark:border-navy-600/50">
            {official.state}
          </span>
        )}
        {official.chamber && (
          <span className="text-[8px] font-black uppercase tracking-wider px-2 py-0.5 rounded border bg-cream-100 dark:bg-navy-700/60 text-navy-500 dark:text-navy-300 border-cream-300 dark:border-navy-600/50">
            {official.chamber}
          </span>
        )}
      </div>

      <div className="pt-2 border-t border-cream-200 dark:border-navy-600/50 space-y-2">
        {lifecycle.detailLabel && (
          <p className="text-[9px] text-navy-400 dark:text-navy-300 font-bold uppercase tracking-wider leading-relaxed">
            {lifecycle.detailLabel}
          </p>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {official.district && (
              <span className="text-[9px] text-navy-400 dark:text-navy-300 font-bold mono">District {official.district}</span>
            )}
          </div>
          <div className="flex items-center gap-3 ml-auto shrink-0">
            {official.accountability_score != null && <CaseFileScoreRow score={official.accountability_score} />}
            <span className="text-[9px] text-gold-600 dark:text-gold-400 font-black uppercase tracking-widest group-hover:text-gold-500">
              {lifecycle.ctaLabel}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

function generatePageNumbers(current: number, total: number): (number | string)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | string)[] = [];
  pages.push(1);
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

export default OfficialsPage;
